import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";

import { Branch } from "../../../../../../models/branch";
import { Filters, Searches, Sorts, Sort } from "../../../../../../models/shipeezi.table";
import BranchService from "../../../../../../services/branch.service";
import { useAppState } from "../../../../../../context/StateContext";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";

export const useActions = () => {
  const { currentUser } = useAppState();

  const [branches, setBranches] = useState<Branch[]>([]);
  const [branch, setBranch] = useState<Branch>();
  const [hasErrors, setHasErrors] = useState<boolean>();
  const [multipleNotification, setMultipleNotification] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["branch.list", page, pageSize, filters, searches, sorting],
    () =>
      BranchService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "warehouses" }, { association: "contacts" }]),
        sortBy: sorting ? JSON.stringify(sorting) : JSON.stringify([["updatedAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        id: JSON.stringify(currentUser?.branches?.map(branch => branch.id)),
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setBranches(response.data);
      },
    }
  );

  const { refetch: getBranchTotal } = useQuery(
    ["branch.total", filters, searches],
    () =>
      BranchService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        id: JSON.stringify(currentUser?.branches?.map(branch => branch.id)),
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: createBranch, isLoading: isCreating } = useMutation(
    ["branch.create"],
    (branch: Branch) => BranchService.create(branch),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getBranchTotal();
      },
      onError: (err: any) => {
        NotificationToast.error(err.response.data);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateBranch, isLoading: isUpdating } = useMutation(
    ["branch.update"],
    ({ id, branch }: { id: number; branch: Branch }) => BranchService.update(id, branch),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getBranchTotal();
      },
      onError: (err: any) => {
        NotificationToast.error(err.response.data);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    variables: {
      branches,
      branch,
      hasErrors,
      page,
      pageSize,
      total,
      isFetching,
      isCreating,
      isUpdating,
    },
    setMultipleNotification,
    setPageSize,
    setBranch,
    createBranch,
    updateBranch,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
  };
};
